// filepath: apps/web/src/features/admin/secrets/SecretReferencesPage.tsx
//
// FR-UI-ADM-10 -- main page composition for the secret reference
// admin editor.
//
// Composes:
//   - useSecrets + usePromotionPendingCount (queries.ts)
//   - PendingReviewBanner (AC-7)
//   - SecretReferencesTable (AC-1)
//   - SecretFormDialog (AC-2 create flow + one-time copy affordance)
//   - SecretImpactDialog (AC-3 + AC-15 impact-before-delete flow)
//
// Five fetch states are handled (FR-UI-CORE-10):
//   - loading: skeleton
//   - fetch_failure: error message
//   - permission_denied: error message
//   - rate_limit: error message with Retry-After
//   - staging_only: StagingOnlyError
//   - stale_version: refetch prompt
//
// INV-5 (central): the create-success screen renders the
// `secret_value` returned by the POST response. The value lives
// only in this component's local state (successSecretValue) and
// is destroyed on dialog close via setSuccessSecretValue(null).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import { StagingOnlyError } from "@/features/admin/StagingOnlyError";
import {
  classifySecretFetchError,
  useCreateSecret,
  useDeleteSecret,
  usePromotionPendingCount,
  useSecretImpact,
  useSecrets,
} from "./queries";
import { SecretReferencesTable } from "./SecretReferencesTable";
import { SecretFormDialog } from "./SecretFormDialog";
import { SecretImpactDialog } from "./SecretImpactDialog";
import { PendingReviewBanner } from "./PendingReviewBanner";
import type { SecretMeta } from "./types";

interface SecretReferencesPageProps {
  environmentKind: string;
  onOpenPromotion?: () => void;
}

export function SecretReferencesPage({
  environmentKind,
  onOpenPromotion,
}: SecretReferencesPageProps): React.ReactElement {
  const { t } = useTranslation();
  const list = useSecrets();
  const createMutation = useCreateSecret();
  const deleteMutation = useDeleteSecret();
  const pending = usePromotionPendingCount();

  // create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [successSecretValue, setSuccessSecretValue] = useState<string | null>(
    null,
  );

  // impact dialog
  const [impactTarget, setImpactTarget] = useState<SecretMeta | null>(null);
  const impactQuery = useSecretImpact(impactTarget?.id ?? null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ─── render branches ──────────────────────────────────────────────────

  if (list.isLoading) {
    return (
      <div
        className="p-6 text-sm text-muted-foreground"
        data-testid="secrets-loading"
      >
        {t("common.loading")}
      </div>
    );
  }

  if (list.error !== null && list.error !== undefined) {
    const classified = classifySecretFetchError(list.error as ApiError);
    if (classified.kind === "permission_denied") {
      return (
        <div
          className="m-6 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700"
          role="alert"
          data-testid="secrets-permission-denied"
        >
          {classified.message}
        </div>
      );
    }
    if (classified.kind === "rate_limit") {
      return (
        <div
          className="m-6 rounded border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900"
          role="alert"
          data-testid="secrets-rate-limit"
        >
          {classified.message}
        </div>
      );
    }
    return (
      <div
        className="m-6 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700"
        role="alert"
        data-testid="secrets-fetch-failure"
      >
        {classified.message}
      </div>
    );
  }

  // ─── main render ──────────────────────────────────────────────────────

  const secrets = list.data?.secrets ?? [];

  const handleCreate = async (req: { name: string }): Promise<void> => {
    setCreateError(null);
    try {
      const result = await createMutation.mutateAsync(req);
      // INV-5: the create response carries secret_value ONCE; cache
      // it locally for the one-time copy affordance, then destroy
      // it on dialog close.
      setSuccessSecretValue(result.secret_value);
    } catch (e) {
      const err = e as { message?: string };
      setCreateError(err.message ?? t("common.error"));
    }
  };

  const handleCloseCreate = (): void => {
    // INV-5 destroy-on-close: wipe the secret_value from React
    // state so it cannot leak via subsequent renders.
    setSuccessSecretValue(null);
    setShowCreate(false);
    setCreateError(null);
  };

  const handleRequestDelete = (row: SecretMeta): void => {
    setImpactTarget(row);
    setDeleteError(null);
  };

  const handleCloseImpact = (): void => {
    setImpactTarget(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!impactTarget) return;
    try {
      await deleteMutation.mutateAsync({
        id: impactTarget.id,
        rowVersion: impactTarget.row_version,
      });
      handleCloseImpact();
    } catch (e) {
      const err = e as { message?: string };
      setDeleteError(err.message ?? t("common.error"));
    }
  };

  return (
    <div className="space-y-4 p-6" data-testid="secrets-page">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t("admin.secrets.title")}</h1>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
          data-testid="secrets-create-button"
        >
          {t("admin.secrets.create_button")}
        </button>
      </header>

      <PendingReviewBanner
        pendingCount={pending.data?.pending_count ?? 0}
        onOpenPromotion={
          onOpenPromotion ??
          (() => {
            /* default: no-op (test harness) */
          })
        }
      />

      <SecretReferencesTable
        rows={secrets}
        onRequestDelete={handleRequestDelete}
        isDeleting={deleteMutation.isPending}
      />

      {showCreate ? (
        <SecretFormDialog
          onClose={handleCloseCreate}
          onSubmit={handleCreate}
          isSubmitting={createMutation.isPending}
          errorMessage={createError}
          successSecretValue={successSecretValue}
        />
      ) : null}

      {impactTarget ? (
        <SecretImpactDialog
          secret={impactTarget}
          impact={impactQuery.data ?? null}
          isLoading={impactQuery.isLoading}
          isDeleting={deleteMutation.isPending}
          onConfirmDelete={() => {
            void handleConfirmDelete();
          }}
          onCancel={handleCloseImpact}
          errorMessage={deleteError}
        />
      ) : null}

      {environmentKind === "production" ? (
        <StagingOnlyError environmentKind={environmentKind} />
      ) : null}
    </div>
  );
}
